import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Request {
  id: number;
  plannerName: string;
  plannerCompany: string;
  customerName: string;
  requestDate: string;
  shootingDate: string;
  shootingType: string;
  location: string;
  status: string;
  memo: string;
  createdAt: string;
}

export default function VendorStudioRequestsPage() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState('전체');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  const menuItems = [
    { id: 'dashboard', icon: 'ri-dashboard-line', label: '대시보드', path: '/vendor-portal' },
    { id: 'orders', icon: 'ri-file-list-3-line', label: '발주서 관리', path: '/vendor-portal/orders' },
    { id: 'schedule', icon: 'ri-calendar-line', label: '촬영 일정 관리', path: '/vendor-portal/studio-schedule' },
    { id: 'ordersheet', icon: 'ri-file-text-line', label: '촬영 오더시트', path: '/vendor-portal/studio-customers' },
    { id: 'staff', icon: 'ri-team-line', label: '작가·스태프 배정', path: '/vendor-portal/studio-staff' },
    { id: 'photos', icon: 'ri-folder-image-line', label: '촬영 사진 관리', path: '/vendor-portal/studio-photos' },
    { id: 'collaboration', icon: 'ri-user-star-line', label: '플래너 협업 관리', path: '/vendor-portal/studio-requests' },
    { id: 'invoice', icon: 'ri-file-text-line', label: '인보이스 관리', path: '/vendor-portal/invoice' },
    { id: 'tax', icon: 'ri-file-list-line', label: '세금계산서 관리', path: '/vendor-portal/tax' },
    { id: 'products', icon: 'ri-shopping-bag-line', label: '상품 관리', path: '/vendor-portal/products' }
  ];

  const requests: Request[] = [
    {
      id: 1,
      plannerName: '김플래너',
      plannerCompany: '드림웨딩',
      customerName: '이지은',
      requestDate: '2024-01-20',
      shootingDate: '2024-02-15',
      shootingType: '본식스냅',
      location: '그랜드웨딩홀',
      status: '승인대기',
      memo: '영상 동시 촬영 가능 여부 확인 필요',
      createdAt: '2024-01-20 14:30'
    },
    {
      id: 2,
      plannerName: '박플래너',
      plannerCompany: '로맨틱웨딩',
      customerName: '김민수',
      requestDate: '2024-01-22',
      shootingDate: '2024-02-20',
      shootingType: '리허설',
      location: '스튜디오 A',
      status: '승인완료',
      memo: '야외 촬영 포함',
      createdAt: '2024-01-22 10:15'
    },
    {
      id: 3,
      plannerName: '최플래너',
      plannerCompany: '프리미엄웨딩',
      customerName: '박영희',
      requestDate: '2024-01-23',
      shootingDate: '2024-03-01',
      shootingType: '스냅+영상',
      location: '로얄웨딩홀',
      status: '승인대기',
      memo: '드론 촬영 요청',
      createdAt: '2024-01-23 16:45'
    },
    {
      id: 4,
      plannerName: '이플래너',
      plannerCompany: '엘리트웨딩',
      customerName: '최수진',
      requestDate: '2024-01-18',
      shootingDate: '2024-02-10',
      shootingType: '본식영상',
      location: '파라다이스웨딩홀',
      status: '거절',
      memo: '일정 중복으로 거절',
      createdAt: '2024-01-18 11:20'
    }
  ];

  const filteredRequests = requests.filter(request => 
    statusFilter === '전체' || request.status === statusFilter
  );

  const statusOptions = ['전체', '승인대기', '승인완료', '거절'];

  const handleApprove = (request: Request) => {
    alert(`${request.customerName} 님의 촬영 요청이 승인되었습니다.`);
    setShowDetailModal(false);
  };

  const handleReject = (request: Request) => {
    const reason = prompt('거절 사유를 입력하세요:');
    if (reason) {
      alert(`${request.customerName} 님의 촬영 요청이 거절되었습니다.`);
      setShowDetailModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 사이드바 */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* 로고 영역 */}
        <div className="p-6 border-b border-gray-200">
          {isSidebarOpen ? (
            <div>
              <h1 className="text-xl font-bold text-gray-800">메이스튜디오</h1>
              <p className="text-sm text-gray-600 mt-1">업체 포털</p>
            </div>
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
          )}
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer ${
                item.path === '/vendor-portal/studio-requests' ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-700'
              }`}
            >
              <i className={`${item.icon} text-lg ${isSidebarOpen ? 'mr-3' : ''}`}></i>
              {isSidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* 하단 사용자 정보 */}
        <div className="p-4 border-t border-gray-200">
          {isSidebarOpen ? (
            <div>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-user-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">김대표</p>
                  <p className="text-xs text-gray-600">메이스튜디오</p>
                </div>
              </div>
              <button className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap cursor-pointer">
                <i className="ri-logout-box-line mr-2"></i>로그아웃
              </button>
            </div>
          ) : (
            <button className="w-full p-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              <i className="ri-logout-box-line text-lg"></i>
            </button>
          )}
        </div>
      </aside>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex flex-col">
        {/* 헤더 */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
              <h2 className="text-xl font-semibold text-gray-900">플래너 협업 관리</h2>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* 컨텐츠 영역 */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-8">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mt-1">플래너의 촬영 요청을 확인하고 승인하세요</p>
            </div>

            {/* 통계 카드 */}
            <div className="grid grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">전체 요청</p>
                    <p className="text-2xl font-bold text-gray-800">{requests.length}건</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-line text-xl text-blue-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">승인 대기</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {requests.filter(r => r.status === '승인대기').length}건
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-time-line text-xl text-orange-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">승인 완료</p>
                    <p className="text-2xl font-bold text-green-600">
                      {requests.filter(r => r.status === '승인완료').length}건
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">거절</p>
                    <p className="text-2xl font-bold text-red-600">
                      {requests.filter(r => r.status === '거절').length}건
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <i className="ri-close-line text-xl text-red-600"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* 필터 */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex gap-2">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                      statusFilter === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* 요청 목록 */}
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div 
                  key={request.id} 
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-star-line text-2xl text-purple-600"></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold text-gray-800">{request.customerName}</h3>
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                            request.status === '승인완료' ? 'bg-green-100 text-green-700' :
                            request.status === '승인대기' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {request.status}
                          </span>
                          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            {request.shootingType}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <i className="ri-building-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{request.plannerCompany} - {request.plannerName}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                            <span>촬영일: {request.shootingDate}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{request.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="text-sm text-gray-600 mb-1">요청 메모</div>
                    <div className="text-sm text-gray-800">{request.memo}</div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div>요청일시: {request.createdAt}</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 px-3 py-1.5 hover:bg-blue-50 rounded transition-colors cursor-pointer font-medium"
                      >
                        상세보기
                      </button>
                      {request.status === '승인대기' && (
                        <>
                          <button
                            onClick={() => handleApprove(request)}
                            className="text-green-600 hover:text-green-800 px-3 py-1.5 hover:bg-green-50 rounded transition-colors cursor-pointer font-medium"
                          >
                            승인
                          </button>
                          <button
                            onClick={() => handleReject(request)}
                            className="text-red-600 hover:text-red-800 px-3 py-1.5 hover:bg-red-50 rounded transition-colors cursor-pointer font-medium"
                          >
                            거절
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredRequests.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <i className="ri-inbox-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">요청이 없습니다.</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* 상세보기 모달 */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">촬영 요청 상세</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">플래너 회사</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.plannerCompany}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">플래너명</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.plannerName}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">고객명</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.customerName}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">촬영 유형</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.shootingType}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">촬영일</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.shootingDate}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">촬영 장소</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.location}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">요청 메모</label>
                <div className="px-3 py-2 bg-gray-50 rounded-lg">{selectedRequest.memo}</div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">상태</label>
                <div className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                  selectedRequest.status === '승인완료' ? 'bg-green-100 text-green-700' :
                  selectedRequest.status === '승인대기' ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {selectedRequest.status}
                </div>
              </div>
            </div>

            {selectedRequest.status === '승인대기' && (
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => handleReject(selectedRequest)}
                  className="px-6 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  거절
                </button>
                <button
                  onClick={() => handleApprove(selectedRequest)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  승인
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}